import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ViewHeader, PortalEmptyState } from "./PortalShared";
import { Plus, Trash2, Send, FileText, CheckCircle, RefreshCw, Briefcase, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CFG = {
  draft:    { label: "Draft",    color: "text-white/40 bg-white/[0.04] border-white/[0.08]" },
  sent:     { label: "Sent",     color: "text-blue-400 bg-blue-500/10 border-blue-500/20" },
  accepted: { label: "Accepted", color: "text-[#00FF88] bg-emerald-500/10 border-emerald-500/20" },
  rejected: { label: "Rejected", color: "text-[#FF3B30] bg-red-500/10 border-red-500/20" },
};

function StatusBadge({ status }) {
  const c = STATUS_CFG[status] || STATUS_CFG.draft;
  return <span className={`px-2 py-0.5 text-[10px] font-bold border rounded-full uppercase tracking-wider ${c.color}`}>{c.label}</span>;
}

const CATEGORY_COLORS = {
  "Demo": "text-orange-400", "Framing": "text-yellow-400", "Electrical": "text-yellow-300",
  "Plumbing": "text-blue-400", "Flooring": "text-emerald-400", "Painting": "text-purple-400",
  "Roofing": "text-[#D4AF37]", "HVAC": "text-cyan-400", "General Labor": "text-white/50",
  "General": "text-white/40",
};

function AISuggestPanel({ form, onApply, onClose }) {
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [sqft, setSqft] = useState("");
  const [budget, setBudget] = useState("");

  const getSuggestions = async () => {
    if (!form.scope_of_work) { toast.error("Fill in Scope of Work first"); return; }
    setSuggesting(true);
    setSuggestions(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/portal-tools/bids/suggest`, {
        job_title: form.job_title || "Construction Project",
        property_address: form.property_address,
        scope_of_work: form.scope_of_work,
        property_type: "residential",
        sqft: sqft ? parseInt(sqft) : null,
        budget_hint: budget ? parseFloat(budget) : null,
      }, { withCredentials: true });
      setSuggestions(res.data);
      setSelected(new Set(res.data.items.map((_, i) => i)));
      toast.success(`${res.data.item_count} line items suggested!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI suggestion failed");
    } finally {
      setSuggesting(false);
    }
  };

  const toggleItem = (i) => setSelected(prev => {
    const s = new Set(prev);
    s.has(i) ? s.delete(i) : s.add(i);
    return s;
  });

  const apply = () => {
    const items = suggestions.items.filter((_, i) => selected.has(i))
      .map(({ description, qty, unit_price, total }) => ({ description, qty, unit_price, total }));
    if (items.length === 0) { toast.error("Select at least one item"); return; }
    onApply(items);
    onClose();
    toast.success(`${items.length} items added to your bid`);
  };

  return (
    <div className="bg-[#0A0A0A] border border-purple-500/30 rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-purple-400" />
          <p className="text-white font-semibold text-sm">AI Bid Suggestions</p>
          <span className="text-[9px] px-1.5 py-0.5 bg-purple-500/10 border border-purple-500/20 text-purple-400 rounded-full">Llama 3.3 70B</span>
        </div>
        <button onClick={onClose} className="text-white/30 hover:text-white text-xs">✕</button>
      </div>

      {!suggestions ? (
        <div className="space-y-3">
          <p className="text-white/40 text-xs">Describe your project and AI will suggest realistic line items with current market pricing.</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-white/40 block mb-1">Square Footage (optional)</label>
              <Input value={sqft} onChange={e => setSqft(e.target.value)} type="number" placeholder="e.g. 1200"
                className="bg-white/[0.03] border-white/[0.08] text-white h-8 text-sm" />
            </div>
            <div>
              <label className="text-[10px] text-white/40 block mb-1">Budget Hint (optional)</label>
              <Input value={budget} onChange={e => setBudget(e.target.value)} type="number" placeholder="e.g. 35000"
                className="bg-white/[0.03] border-white/[0.08] text-white h-8 text-sm" />
            </div>
          </div>
          <button onClick={getSuggestions} disabled={suggesting}
            data-testid="ai-suggest-btn"
            className="w-full py-2.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-bold text-sm rounded-lg transition-all flex items-center justify-center gap-2">
            {suggesting
              ? <><RefreshCw className="w-4 h-4 animate-spin" /> Generating…</>
              : <><Sparkles className="w-4 h-4" /> Generate Suggestions</>}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-white/40 text-xs">
              {suggestions.items.length} items · Est. <span className="text-[#D4AF37] font-bold">${suggestions.total.toLocaleString()}</span>
            </p>
            <div className="flex gap-2">
              <button onClick={() => setSelected(new Set(suggestions.items.map((_, i) => i)))} className="text-[10px] text-purple-400 hover:underline">Select all</button>
              <button onClick={() => setSelected(new Set())} className="text-[10px] text-white/30 hover:underline">None</button>
            </div>
          </div>
          <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
            {suggestions.items.map((item, i) => (
              <button key={i} onClick={() => toggleItem(i)}
                data-testid={`suggest-item-${i}`}
                className={`w-full flex items-center gap-3 p-2.5 border rounded-lg transition-all text-left ${selected.has(i) ? 'bg-purple-500/[0.08] border-purple-500/20' : 'bg-white/[0.01] border-white/[0.04] opacity-40 hover:opacity-70'}`}>
                <div className={`w-3 h-3 rounded border flex-shrink-0 flex items-center justify-center ${selected.has(i) ? 'bg-purple-500 border-purple-500' : 'border-white/20'}`}>
                  {selected.has(i) && <span className="text-white text-[8px]">✓</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-xs truncate">{item.description}</p>
                  <p className={`text-[10px] ${CATEGORY_COLORS[item.category] || 'text-white/30'}`}>
                    {item.category} · {item.qty} {item.unit} @ ${item.unit_price}
                  </p>
                </div>
                <span className="text-white/60 text-xs font-mono flex-shrink-0">${item.total.toLocaleString()}</span>
              </button>
            ))}
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={getSuggestions} disabled={suggesting}
              className="flex-1 py-2 text-xs text-purple-400 border border-purple-500/20 rounded-lg hover:bg-purple-500/[0.06] transition-all flex items-center justify-center gap-1.5 disabled:opacity-50">
              <RefreshCw className={`w-3 h-3 ${suggesting ? 'animate-spin' : ''}`} /> Regenerate
            </button>
            <button onClick={apply} data-testid="apply-suggestions-btn"
              className="flex-[2] py-2 text-xs bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-lg transition-all flex items-center justify-center gap-1.5">
              <Plus className="w-3 h-3" /> Add {selected.size} Selected Item{selected.size !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function BidBuilder({ bids: initialBids = [], onBidCreated }) {
  const [bids, setBids] = useState(initialBids);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [form, setForm] = useState({
    job_title: "", client_name: "", property_address: "", scope_of_work: "",
    start_date: "", duration_days: "", warranty_months: 12, notes: ""
  });
  const [lineItems, setLineItems] = useState([{ description: "", qty: 1, unit_price: 0, total: 0 }]);

  const updateItem = (i, field, val) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[i] = { ...updated[i], [field]: val };
      updated[i].total = Number(updated[i].qty) * Number(updated[i].unit_price);
      return updated;
    });
  };

  const applyAISuggestions = (items) => {
    const formatted = items.map(item => ({
      description: item.description,
      qty: item.qty,
      unit_price: item.unit_price,
      total: item.total,
    }));
    setLineItems(prev => {
      const isEmpty = prev.length === 1 && !prev[0].description && prev[0].unit_price === 0;
      return isEmpty ? formatted : [...prev, ...formatted];
    });
  };

  const grandTotal = lineItems.reduce((s, i) => s + (Number(i.qty) * Number(i.unit_price)), 0);

  const handleSubmit = async () => {
    if (!form.job_title || !form.client_name) { toast.error("Job title and client name required"); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/portal-tools/bids`, {
        ...form, line_items: lineItems.map(i => ({ ...i, total: Number(i.qty) * Number(i.unit_price) }))
      }, { withCredentials: true });
      setBids(prev => [res.data, ...prev]);
      toast.success("Bid created");
      setShowForm(false);
      setShowAI(false);
      setForm({ job_title: "", client_name: "", property_address: "", scope_of_work: "", start_date: "", duration_days: "", warranty_months: 12, notes: "" });
      setLineItems([{ description: "", qty: 1, unit_price: 0, total: 0 }]);
      if (onBidCreated) onBidCreated(res.data);
    } catch (err) { toast.error("Failed to save bid"); }
    finally { setSaving(false); }
  };

  const updateStatus = async (bidId, status) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/portal-tools/bids/${bidId}/status`, { status }, { withCredentials: true });
      setBids(prev => prev.map(b => b.bid_id === bidId ? { ...b, status } : b));
      toast.success(`Bid marked as ${status}`);
    } catch { toast.error("Update failed"); }
  };

  if (showForm) return (
    <div>
      <ViewHeader title="New Bid Proposal" subtitle="Build a professional bid for your client" action="Back to Bids" onAction={() => { setShowForm(false); setShowAI(false); }} />
      <div className="grid grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5 space-y-4">
            <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold">Project Details</p>
            {[
              ["job_title","Job Title"], ["client_name","Client Name"],
              ["property_address","Property Address"], ["start_date","Start Date","date"],
              ["duration_days","Duration (days)","number"]
            ].map(([k, l, t = "text"]) => (
              <div key={k}>
                <label className="text-[10px] text-white/40 block mb-1">{l}</label>
                <Input type={t} value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} className="bg-white/[0.03] border-white/[0.08] text-white h-8 text-sm" />
              </div>
            ))}
            <div>
              <label className="text-[10px] text-white/40 block mb-1">Scope of Work</label>
              <textarea value={form.scope_of_work} onChange={e => setForm(f => ({ ...f, scope_of_work: e.target.value }))} rows={3}
                className="w-full bg-white/[0.03] border border-white/[0.08] rounded px-3 py-2 text-sm text-white resize-none focus:outline-none focus:border-[#D4AF37]/50"
                placeholder="Describe the work to be done…" />
            </div>
          </div>

          {showAI ? (
            <AISuggestPanel form={form} onApply={applyAISuggestions} onClose={() => setShowAI(false)} />
          ) : (
            <button onClick={() => setShowAI(true)} data-testid="open-ai-suggest-btn"
              className="w-full py-2.5 border border-purple-500/30 bg-purple-500/[0.04] hover:bg-purple-500/[0.08] text-purple-400 font-medium text-sm rounded-xl transition-all flex items-center justify-center gap-2">
              <Sparkles className="w-4 h-4" /> AI Suggest Line Items
            </button>
          )}
        </div>

        <div className="space-y-4">
          <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold">Line Items</p>
              <button onClick={() => setLineItems(p => [...p, { description: "", qty: 1, unit_price: 0, total: 0 }])}
                className="text-xs text-[#D4AF37] hover:text-[#C4A030] flex items-center gap-1">
                <Plus className="w-3 h-3" /> Add Line
              </button>
            </div>
            <div className="grid grid-cols-12 text-[10px] text-white/20 uppercase tracking-wider px-1">
              <span className="col-span-5">Description</span>
              <span className="col-span-2 text-center">Qty</span>
              <span className="col-span-3 text-right">Unit Price</span>
              <span className="col-span-2 text-right">Total</span>
            </div>
            {lineItems.map((item, i) => (
              <div key={i} className="grid grid-cols-12 gap-1 items-center">
                <Input value={item.description} onChange={e => updateItem(i, "description", e.target.value)}
                  className="col-span-5 bg-white/[0.02] border-white/[0.06] text-white h-7 text-xs" placeholder="Labor / materials…" />
                <Input type="number" value={item.qty} onChange={e => updateItem(i, "qty", e.target.value)}
                  className="col-span-2 bg-white/[0.02] border-white/[0.06] text-white h-7 text-xs text-center" />
                <Input type="number" value={item.unit_price} onChange={e => updateItem(i, "unit_price", e.target.value)}
                  className="col-span-3 bg-white/[0.02] border-white/[0.06] text-white h-7 text-xs text-right" />
                <div className="col-span-1 text-right text-xs text-white/60">${(item.qty * item.unit_price).toFixed(0)}</div>
                <button onClick={() => setLineItems(p => p.filter((_, j) => j !== i))} className="col-span-1 text-white/20 hover:text-[#FF3B30]">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
            <div className="flex justify-between items-center pt-3 border-t border-white/[0.06]">
              <span className="text-white/40 text-sm">Total Bid Value</span>
              <span className="text-[#D4AF37] font-bold text-xl">${grandTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
            </div>
          </div>
          <Button onClick={handleSubmit} disabled={saving} className="w-full bg-[#D4AF37] hover:bg-[#C4A030] text-black font-bold" data-testid="save-bid-btn">
            {saving ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <><FileText className="w-4 h-4 mr-2" /> Save Bid Proposal</>}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <ViewHeader title="Bid Builder" subtitle="Create and manage professional bid proposals" action="New Bid" onAction={() => setShowForm(true)} actionTestId="create-bid-btn" />
      {bids.length === 0 ? (
        <PortalEmptyState icon={Briefcase} title="No bids yet" desc="Create your first professional bid proposal" action="Build a Bid" onAction={() => setShowForm(true)} />
      ) : (
        <div className="space-y-3">
          {bids.map(bid => (
            <div key={bid.bid_id} className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-4 flex items-center gap-4" data-testid={`bid-row-${bid.bid_id}`}>
              <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Briefcase className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm truncate">{bid.job_title}</p>
                <p className="text-white/40 text-xs">{bid.client_name} · {bid.property_address}</p>
                <p className="text-white/30 text-xs mt-0.5">{new Date(bid.created_at).toLocaleDateString()}</p>
              </div>
              <div className="text-right">
                <p className="text-[#D4AF37] font-bold text-lg">${bid.total?.toLocaleString()}</p>
                <StatusBadge status={bid.status} />
              </div>
              <div className="flex gap-1.5 ml-3">
                {bid.status === "draft" && (
                  <button onClick={() => updateStatus(bid.bid_id, "sent")} className="text-xs px-2 py-1 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded hover:bg-blue-500/20">
                    <Send className="w-3 h-3" />
                  </button>
                )}
                {bid.status === "sent" && (
                  <button onClick={() => updateStatus(bid.bid_id, "accepted")} className="text-xs px-2 py-1 bg-emerald-500/10 border border-emerald-500/20 text-[#00FF88] rounded hover:bg-emerald-500/20">
                    <CheckCircle className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
